import slugify from "slugify";
import type { Entry } from "contentful";
import type { Category } from "@graphql/generated/components";
import type { IPagePathFields } from "@contentful-api/types/contentful";
import type { pagePathData } from "@store/types";

const flatCategory = ({ name, parent }: Category, acc: string[] = [], locale): string[] => {
    const parsedName = slugify(name?.toLowerCase() || "");

    if (!parent) {
        // We don't want to include the root category in the breadcrumbs i.e. "my-root"
        // We also want to reverse the array so that the breadcrumbs are in the correct order
        return acc.reverse();
    }

    return flatCategory(parent, [...acc, slugify(parsedName)], locale);
};

const checkParentLocale = (category: Category, locale: string) => {
    if (category.parent) {
        return checkParentLocale(category.parent, locale);
    } else {
        return category.name?.split(" ")[0] === locale.split("-")[1];
    }
};

const normalizeCategories = (categories: Category[], locale: string): string[][] => {
    return categories
        ?.filter((it) => checkParentLocale(it, locale))
        ?.map((item) => flatCategory(item, [], locale));
};

const filterCategoriesByPaths = (normalizedCategories: string[][], availablePaths: string[]) =>
    normalizedCategories?.filter((category) => {
        return availablePaths.includes(`/${category.join("/")}`);
    });

interface IGetProductBreadcrumbs {
    productCategories: Category[];
    allPagePaths: Entry<IPagePathFields>[];
    locale: string;
    fallbackLocale?: string;
}

export const getProductBreadcrumbs = ({
    productCategories,
    allPagePaths,
    locale,
    fallbackLocale = "en",
}: IGetProductBreadcrumbs) => {
    const paths = allPagePaths
        ?.map((item) => {
            const isPageProductListingPage =
                item?.fields?.page?.[locale]?.sys?.contentType?.sys?.id ===
                    "pageProductListingPage" ||
                item?.fields?.page?.[fallbackLocale]?.sys?.contentType?.sys?.id ===
                    "pageProductListingPage";

            if (isPageProductListingPage) {
                return (
                    item?.fields?.completeSlug[locale] ||
                    item?.fields?.completeSlug[fallbackLocale] ||
                    null
                );
            }
            return null;
        })
        ?.filter?.(Boolean);

    const normalizedCategories = normalizeCategories(productCategories, locale);

    const breadcrumbs = filterCategoriesByPaths(normalizedCategories, paths);

    // We want to return the longest breadcrumb
    return breadcrumbs?.sort((a, b) => b.length - a.length)?.[0];
};

/**
 * This function analyzes the first two levels of each path, counts their occurrences,
 * and returns the most frequent pair. It ignores single-category paths and deeper levels.
 * It's used to determine the most common algolia categories with depth of two.
 *
 * Example:
 * Input:
 * [
 *     "women",
 *     "women>featured",
 *     "women>featured>extra-wide-collection",
 *     "women>featured>waterproof-collection",
 *     "women>shoes",
 *     "women>shoes>outdoor",
 *     "golf",
 *     "women>shoes>sneakers",
 *     "women>shoes>hiking-and-trail"
 * ]
 * Returns: ["women", "shoes"]
 */
export const findMostCommonCategories = (paths: string[]): string[] => {
    const categoryCounts = new Map<string, number>();
    let maxCount = 0;
    let mostCommonCategories: string[] = [];

    for (const path of paths) {
        const categories = path.split(">").slice(0, 2);
        if (categories.length === 2) {
            const key = categories.join(">");
            const count = (categoryCounts.get(key) || 0) + 1;
            categoryCounts.set(key, count);

            if (count > maxCount) {
                maxCount = count;
                mostCommonCategories = categories;
            }
        }
    }

    return mostCommonCategories;
};

export const getValidPath = (pagePathData: pagePathData, urlArray: string[], locale: string) => {
    if (!pagePathData || !urlArray?.length) return [];

    const localeData = {
        ...(pagePathData["en"] || {}),
        ...(pagePathData[locale] || {}),
    };

    const joinedPath = "/" + urlArray.join("/");
    const hierarchy = localeData[joinedPath];

    if (!hierarchy) return [];

    const validSegmentsWithLinks = [];

    for (let i = 0; i < urlArray.length; i++) {
        const segment = urlArray[i];
        const formattedSegment = segment.includes("-") ? segment.replace(/-/g, " ") : segment;
        const path = "/" + urlArray.slice(0, i + 1).join("/");

        if (hierarchy.includes(path)) {
            validSegmentsWithLinks.push({
                label: formattedSegment,
                link: path,
            });
        }
    }

    return validSegmentsWithLinks;
};
